import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const TOOLTIP_MARGIN = 8;
const EMPTY_ARRAY = [];

const InfoTooltip = ({
  content,
  children,
  enabled = true,
  offset = 8,
  placement = 'top',
  fallbackPlacements = EMPTY_ARRAY,
  autoFlip = false,
  className = '',
}) => {
  const [visible, setVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [positionReady, setPositionReady] = useState(false);
  const wrapperRef = useRef(null);
  const tooltipRef = useRef(null);
  const placementRef = useRef(placement);
  const fallbackPlacementsRef = useRef(fallbackPlacements);
  const autoFlipRef = useRef(autoFlip);
  const offsetRef = useRef(offset);

  useLayoutEffect(() => {
    placementRef.current = placement;
    fallbackPlacementsRef.current = fallbackPlacements;
    autoFlipRef.current = autoFlip;
    offsetRef.current = offset;
  }, [autoFlip, fallbackPlacements, offset, placement]);

  const updatePosition = useCallback(() => {
    if (!wrapperRef.current || !tooltipRef.current) {
      return;
    }

    const anchorRect = wrapperRef.current.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const preferredPlacement = placementRef.current;
    const currentFallbacks = fallbackPlacementsRef.current || EMPTY_ARRAY;
    const currentOffset = offsetRef.current;
    const shouldAutoFlip = autoFlipRef.current;

    let left = anchorRect.left + anchorRect.width / 2 - tooltipRect.width / 2;
    const maxLeft = Math.max(TOOLTIP_MARGIN, viewportWidth - tooltipRect.width - TOOLTIP_MARGIN);
    left = Math.min(Math.max(left, TOOLTIP_MARGIN), maxLeft);

    let resolvedPlacement = preferredPlacement;
    const topCandidate = anchorRect.top - tooltipRect.height - currentOffset;
    if (
      shouldAutoFlip
      && preferredPlacement === 'top'
      && currentFallbacks.includes('bottom')
      && topCandidate < TOOLTIP_MARGIN
    ) {
      resolvedPlacement = 'bottom';
    }

    let top = resolvedPlacement === 'bottom'
      ? anchorRect.bottom + currentOffset
      : topCandidate;

    if (resolvedPlacement === 'top') {
      top = Math.max(TOOLTIP_MARGIN, top);
    }

    setPosition({ top, left });
    setPositionReady(true);
  }, []);

  useLayoutEffect(() => {
    if (!visible) {
      return undefined;
    }

    updatePosition();
    const handleReposition = () => updatePosition();
    window.addEventListener('resize', handleReposition);
    window.addEventListener('scroll', handleReposition, true);

    return () => {
      window.removeEventListener('resize', handleReposition);
      window.removeEventListener('scroll', handleReposition, true);
    };
  }, [updatePosition, visible]);

  const showTooltip = () => {
    if (!enabled) {
      return;
    }
    setPositionReady(false);
    setVisible(true);
  };

  const hideTooltip = () => {
    setVisible(false);
  };

  return (
    <span
      className={`info-popover-wrapper${className ? ` ${className}` : ''}`}
      ref={wrapperRef}
      onMouseEnter={showTooltip}
      onMouseLeave={hideTooltip}
      onFocus={showTooltip}
      onBlur={hideTooltip}
    >
      {children}
      {enabled && visible
        ? createPortal(
          <span
            className="info-popover info-popover--portal"
            role="tooltip"
            ref={tooltipRef}
            style={{
              top: `${position.top}px`,
              left: `${position.left}px`,
              opacity: positionReady ? 1 : 0,
            }}
          >
            {content}
          </span>,
          document.body,
        )
        : null}
    </span>
  );
};

export default InfoTooltip;
